import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../api/client";

const emptyForm = {
  itemCode: "",
  itemName: "",
  hsnCode: "",
  itemType: "Finished Goods",
  categoryId: "",
  groupId: "",
  unitId: "",
  unit: "Nos",
  gstRateId: "",
  costPrice: 0,
  salePrice: 0,
  gstPercent: 18,
  openingStock: 0,

  brandName: "",
  barcode: "",
  sku: "",
  warehouseId: "",
  warehouseName: "",

  maintainStock: true,
  allowNegativeStock: false,
  reorderLevel: 0,
  reorderQty: 0,
  minStock: 0,
  maxStock: 0,

  isBatchEnabled: false,
  isSerialEnabled: false,
  isWarrantyApplicable: false,
  isExpiryApplicable: false,

  itemImageUrl: "",
  notes: "",
  itemStatus: "Active",

  baseUnitId: "",
  purchaseUnitId: "",
  salesUnitId: "",
  purchaseConversion: 1,
  salesConversion: 1,
};

const companyId = () => Number(localStorage.getItem("companyId") || 0);

const val = (r, ...keys) => {
  for (const k of keys) {
    const x = r?.[k];
    if (x !== undefined && x !== null) return x;
  }
  return "";
};

const safeText = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (v?.message) return String(v.message);
  if (v?.title) return String(v.title);
  return "";
};

const errText = (e, fb) => {
  const data = e?.response?.data;
  if (typeof data === "string") return data;
  if (data?.message) return data.message;
  if (data?.title) return data.title;
  return fb;
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const styles = {
  page: { fontSize: 11, minHeight: "100%", padding: 0 },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "8px 12px",
  },
  title: { fontSize: 13, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 600 },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 800,
    color: "#0f766e",
    borderBottom: "1px solid #e9ecef",
    paddingBottom: 4,
    margin: "8px 0 6px",
  },
  tableWrap: {
    maxHeight: 430,
    overflowY: "auto",
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, minWidth: 2100, marginBottom: 0 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    color: "#fff",
    background: "#07111f",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
};

export default function ItemsPage() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const navigate = useNavigate();

  const [masters, setMasters] = useState({
    categories: [],
    groups: [],
    units: [],
    gstRates: [],
    warehouses: [],
  });

  const [quick, setQuick] = useState({
    categoryName: "",
    groupName: "",
    unitName: "",
    unitSymbol: "",
    warehouseName: "",
  });

  useEffect(() => {
    loadMasters();
    load();
  }, []);

  const loadMasters = async () => {
    try {
      const cid = companyId();

      const [c, g, u, gst, wh] = await Promise.allSettled([
        client.get(`/inventory/item-categories?companyId=${cid}`),
        client.get(`/inventory/item-groups?companyId=${cid}`),
        client.get(`/inventory/units?companyId=${cid}`),
        client.get("/inventory/gst-rates"),
        client.get(`/inventory/warehouses?companyId=${cid}`),
      ]);

      setMasters({
        categories:
          c.status === "fulfilled" && Array.isArray(c.value.data)
            ? c.value.data
            : [],
        groups:
          g.status === "fulfilled" && Array.isArray(g.value.data)
            ? g.value.data
            : [],
        units:
          u.status === "fulfilled" && Array.isArray(u.value.data)
            ? u.value.data
            : [],
        gstRates:
          gst.status === "fulfilled" && Array.isArray(gst.value.data)
            ? gst.value.data
            : [],
        warehouses:
          wh.status === "fulfilled" && Array.isArray(wh.value.data)
            ? wh.value.data
            : [],
      });
    } catch (e) {
      alert(errText(e, "Unable to load item masters"));
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await client.get(`/inventory/items?companyId=${companyId()}`);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      alert(errText(e, "Unable to load items"));
    } finally {
      setLoading(false);
    }
  };

  const gstOptions = useMemo(
    () =>
      masters.gstRates.map((r) => ({
        id: val(r, "Id", "id"),
        name: safeText(
          val(r, "RateName", "rateName", "GSTRateName", "gstRateName"),
        ),
        percent: num(val(r, "GSTPercent", "gstPercent")),
      })),
    [masters.gstRates],
  );

  const filteredRows = useMemo(() => {
    const s = search.trim().toLowerCase();

    return rows.filter((r) => {
      const status = safeText(val(r, "ItemStatus", "itemStatus") || "Active");
      const type = safeText(val(r, "ItemType", "itemType"));
      const categoryId = String(val(r, "CategoryId", "categoryId") || "");

      const text = [
        val(r, "ItemCode", "itemCode"),
        val(r, "ItemName", "itemName"),
        val(r, "HSNCode", "hsnCode"),
        val(r, "CategoryName", "categoryName"),
        val(r, "GroupName", "groupName"),
        val(r, "BrandName", "brandName"),
        val(r, "Barcode", "barcode"),
        val(r, "SKU", "sku"),
        val(r, "ItemType", "itemType"),
        val(r, "WarehouseName", "warehouseName"),
      ]
        .map(safeText)
        .join(" ")
        .toLowerCase();

      return (
        (!s || text.includes(s)) &&
        (!filterType || type === filterType) &&
        (!filterStatus || status === filterStatus) &&
        (!filterCategory || categoryId === String(filterCategory))
      );
    });
  }, [rows, search, filterType, filterStatus, filterCategory]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [search, filterType, filterStatus, filterCategory, pageSize]);

  const onGstChange = (id) => {
    const rate = gstOptions.find((x) => String(x.id) === String(id));
    setForm({ ...form, gstRateId: id, gstPercent: rate ? rate.percent : 0 });
  };

  const onUnitChange = (id) => {
    const unit = masters.units.find(
      (x) => String(val(x, "Id", "id")) === String(id),
    );
    setForm({
      ...form,
      unitId: id,
      baseUnitId: id,
      purchaseUnitId: form.purchaseUnitId || id,
      salesUnitId: form.salesUnitId || id,
      unit:
        safeText(val(unit, "UnitSymbol", "unitSymbol")) ||
        safeText(val(unit, "UnitName", "unitName")) ||
        "Nos",
    });
  };

  const onWarehouseChange = (id) => {
    const wh = masters.warehouses.find(
      (x) => String(val(x, "Id", "id")) === String(id),
    );
    setForm({
      ...form,
      warehouseId: id,
      warehouseName: safeText(val(wh, "WarehouseName", "warehouseName")),
    });
  };

  const setItemType = (itemType) => {
    const isService = itemType === "Service";
    setForm({
      ...form,
      itemType,
      maintainStock: isService ? false : form.maintainStock,
      openingStock: isService ? 0 : form.openingStock,
      warehouseId: isService ? "" : form.warehouseId,
      warehouseName: isService ? "" : form.warehouseName,
      reorderLevel: isService ? 0 : form.reorderLevel,
      reorderQty: isService ? 0 : form.reorderQty,
      minStock: isService ? 0 : form.minStock,
      maxStock: isService ? 0 : form.maxStock,
      isBatchEnabled: isService ? false : form.isBatchEnabled,
      isSerialEnabled: isService ? false : form.isSerialEnabled,
      isWarrantyApplicable: isService ? false : form.isWarrantyApplicable,
      isExpiryApplicable: isService ? false : form.isExpiryApplicable,
    });
  };

  const saveMaster = async (type) => {
    try {
      const cid = companyId();

      if (type === "category") {
        if (!quick.categoryName.trim()) return alert("Enter category name");
        await client.post("/inventory/item-categories", {
          companyId: cid,
          categoryName: quick.categoryName.trim(),
        });
        setQuick({ ...quick, categoryName: "" });
      }

      if (type === "group") {
        if (!quick.groupName.trim()) return alert("Enter group name");
        await client.post("/inventory/item-groups", {
          companyId: cid,
          groupName: quick.groupName.trim(),
        });
        setQuick({ ...quick, groupName: "" });
      }

      if (type === "unit") {
        if (!quick.unitName.trim()) return alert("Enter unit name");
        await client.post("/inventory/units", {
          companyId: cid,
          unitName: quick.unitName.trim(),
          unitSymbol: quick.unitSymbol.trim() || quick.unitName.trim(),
        });
        setQuick({ ...quick, unitName: "", unitSymbol: "" });
      }

      if (type === "warehouse") {
        if (!quick.warehouseName.trim()) return alert("Enter warehouse name");

        const warehouseName = quick.warehouseName.trim();

        const warehouseCode =
          "WH-" +
          warehouseName
            .replace(/[^a-zA-Z0-9]/g, "")
            .substring(0, 6)
            .toUpperCase() +
          "-" +
          Date.now().toString().slice(-4);

        await client.post("/inventory/warehouses", {
          companyId: cid,
          warehouseCode,
          warehouseName,
        });

        setQuick({ ...quick, warehouseName: "" });
      }

      await loadMasters();
    } catch (e) {
      alert(errText(e, "Unable to save master"));
    }
  };

  const generateItemCode = async () => {
    try {
      const res = await client.get(
        `/inventory/items/next-code?companyId=${companyId()}`,
      );
      const code = safeText(
        val(res.data, "itemCode", "ItemCode", "code", "Code"),
      );
      setForm({
        ...form,
        itemCode: code || `ITM${Date.now().toString().slice(-6)}`,
      });
    } catch {
      setForm({ ...form, itemCode: `ITM${Date.now().toString().slice(-6)}` });
    }
  };

  const generateBarcode = () => {
    const code = form.itemCode || `ITM${Date.now().toString().slice(-6)}`;
    const barcode = `${companyId()}${Date.now().toString().slice(-9)}`;
    setForm({
      ...form,
      itemCode: form.itemCode || code,
      barcode,
      sku:
        form.sku ||
        `${code}-${safeText(form.itemType).replaceAll(" ", "").toUpperCase()}`,
    });
  };

  const validate = () => {
    if (!safeText(form.itemCode).trim()) return "Item Code is required";
    if (!safeText(form.itemName).trim()) return "Item Name is required";
    if (!safeText(form.hsnCode).trim()) return "HSN / SAC Code is required";
    if (!form.categoryId) return "Please select Item Category";
    if (!form.groupId) return "Please select Item Group";
    if (!form.unitId) return "Please select Unit of Measure";
    if (!form.gstRateId) return "Please select GST Rate";
    if (num(form.costPrice) < 0) return "Cost price cannot be negative";
    if (num(form.salePrice) < 0) return "Sale price cannot be negative";
    if (num(form.openingStock) < 0) return "Opening stock cannot be negative";
    if (!form.maintainStock && num(form.openingStock) > 0) {
      return "Opening stock should be 0 for non-stock/service item";
    }
    return "";
  };

  const save = async () => {
    const msg = validate();
    if (msg) return alert(msg);

    const payload = {
      ...form,
      companyId: companyId(),
      categoryId: Number(form.categoryId),
      groupId: Number(form.groupId),
      unitId: Number(form.unitId),
      gstRateId: Number(form.gstRateId),
      warehouseId: form.warehouseId ? Number(form.warehouseId) : null,

      baseUnitId: form.baseUnitId
        ? Number(form.baseUnitId)
        : Number(form.unitId),
      purchaseUnitId: form.purchaseUnitId
        ? Number(form.purchaseUnitId)
        : Number(form.unitId),
      salesUnitId: form.salesUnitId
        ? Number(form.salesUnitId)
        : Number(form.unitId),

      costPrice: num(form.costPrice),
      salePrice: num(form.salePrice),
      gstPercent: num(form.gstPercent),
      openingStock: num(form.openingStock),

      reorderLevel: num(form.reorderLevel),
      reorderQty: num(form.reorderQty),
      minStock: num(form.minStock),
      maxStock: num(form.maxStock),
      purchaseConversion: num(form.purchaseConversion) || 1,
      salesConversion: num(form.salesConversion) || 1,

      maintainStock: Boolean(form.maintainStock),
      allowNegativeStock: Boolean(form.allowNegativeStock),
      isBatchEnabled: Boolean(form.isBatchEnabled),
      isSerialEnabled: Boolean(form.isSerialEnabled),
      isWarrantyApplicable: Boolean(form.isWarrantyApplicable),
      isExpiryApplicable: Boolean(form.isExpiryApplicable),
    };

    try {
      setSaving(true);

      if (editingId) await client.put(`/inventory/items/${editingId}`, payload);
      else await client.post("/inventory/items", payload);

      setForm(emptyForm);
      setEditingId(null);
      await load();
    } catch (e) {
      alert(errText(e, "Unable to save item"));
    } finally {
      setSaving(false);
    }
  };

  const editRow = (r) => {
    setEditingId(val(r, "Id", "id"));

    setForm({
      ...emptyForm,
      itemCode: safeText(val(r, "ItemCode", "itemCode")),
      itemName: safeText(val(r, "ItemName", "itemName")),
      hsnCode: safeText(val(r, "HSNCode", "hsnCode")),
      itemType: safeText(val(r, "ItemType", "itemType")) || "Finished Goods",

      categoryId: val(r, "CategoryId", "categoryId") || "",
      groupId: val(r, "GroupId", "groupId") || "",
      unitId: val(r, "UnitId", "unitId") || "",
      unit: safeText(val(r, "Unit", "unit")) || "Nos",
      gstRateId: val(r, "GSTRateId", "gstRateId") || "",

      costPrice: num(val(r, "CostPrice", "costPrice")),
      salePrice: num(val(r, "SalePrice", "salePrice")),
      gstPercent: num(val(r, "GSTPercent", "gstPercent", "TaxPercent")) || 18,
      openingStock: num(val(r, "OpeningStock", "openingStock", "OpeningQty")),

      brandName: safeText(val(r, "BrandName", "brandName")),
      barcode: safeText(val(r, "Barcode", "barcode")),
      sku: safeText(val(r, "SKU", "sku")),
      warehouseId: val(r, "WarehouseId", "warehouseId") || "",
      warehouseName: safeText(val(r, "WarehouseName", "warehouseName")),

      maintainStock: Boolean(val(r, "MaintainStock", "maintainStock") ?? true),
      allowNegativeStock: Boolean(
        val(r, "AllowNegativeStock", "allowNegativeStock"),
      ),
      reorderLevel: num(val(r, "ReorderLevel", "reorderLevel")),
      reorderQty: num(val(r, "ReorderQty", "reorderQty")),
      minStock: num(val(r, "MinStock", "minStock")),
      maxStock: num(val(r, "MaxStock", "maxStock")),

      isBatchEnabled: Boolean(val(r, "IsBatchEnabled", "isBatchEnabled")),
      isSerialEnabled: Boolean(val(r, "IsSerialEnabled", "isSerialEnabled")),
      isWarrantyApplicable: Boolean(
        val(r, "IsWarrantyApplicable", "isWarrantyApplicable"),
      ),
      isExpiryApplicable: Boolean(
        val(r, "IsExpiryApplicable", "isExpiryApplicable"),
      ),

      itemImageUrl: safeText(val(r, "ItemImageUrl", "itemImageUrl")),
      notes: safeText(val(r, "Notes", "notes")),
      itemStatus: safeText(val(r, "ItemStatus", "itemStatus")) || "Active",

      baseUnitId:
        val(r, "BaseUnitId", "baseUnitId") || val(r, "UnitId", "unitId") || "",
      purchaseUnitId:
        val(r, "PurchaseUnitId", "purchaseUnitId") ||
        val(r, "UnitId", "unitId") ||
        "",
      salesUnitId:
        val(r, "SalesUnitId", "salesUnitId") ||
        val(r, "UnitId", "unitId") ||
        "",
      purchaseConversion:
        num(val(r, "PurchaseConversion", "purchaseConversion")) || 1,
      salesConversion: num(val(r, "SalesConversion", "salesConversion")) || 1,
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancel = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const deleteRow = async (r) => {
    if (!window.confirm("Deactivate this item?")) return;
    try {
      await client.delete(`/inventory/items/${val(r, "Id", "id")}`);
      await load();
    } catch (e) {
      alert(errText(e, "Unable to deactivate item"));
    }
  };

  const openLedger = (r) => {
    const itemId = val(r, "Id", "id", "ItemId", "itemId");

    if (!itemId) {
      alert("Item id missing");
      return;
    }

    navigate(`/stock-ledger?itemId=${itemId}`);
  };

  const input = (
    key,
    label,
    type = "text",
    col = "col-md-2",
    disabled = false,
  ) => (
    <div className={col}>
      <label style={styles.label}>{label}</label>
      <input
        type={type}
        className="form-control form-control-sm"
        style={styles.input}
        value={form[key] ?? ""}
        placeholder={label}
        disabled={disabled}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      />
    </div>
  );

  const checkbox = (key, label, disabled = false) => (
    <div className="form-check me-3 mb-1">
      <input
        type="checkbox"
        className="form-check-input"
        id={key}
        checked={Boolean(form[key])}
        disabled={disabled}
        onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
      />
      <label
        className="form-check-label"
        htmlFor={key}
        style={{ fontSize: 11 }}
      >
        {label}
      </label>
    </div>
  );

  const select = (key, label, list, textKeys, onChange, disabled = false) => (
    <div className="col-md-2">
      <label style={styles.label}>{label}</label>
      <select
        className="form-select form-select-sm"
        style={styles.input}
        value={form[key] ?? ""}
        disabled={disabled}
        onChange={(e) =>
          onChange
            ? onChange(e.target.value)
            : setForm({ ...form, [key]: e.target.value })
        }
      >
        <option value="">Select {label}</option>
        {list.map((x, idx) => {
          const id = val(x, "Id", "id");
          const text = textKeys
            .map((k) =>
              safeText(val(x, k, k.charAt(0).toLowerCase() + k.slice(1))),
            )
            .filter(Boolean)
            .join(" - ");

          return (
            <option key={id || idx} value={id}>
              {text || `Option ${idx + 1}`}
            </option>
          );
        })}
      </select>
    </div>
  );

  const serviceItem = form.itemType === "Service";
  const openingValue = num(form.openingStock) * num(form.costPrice);

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h6 style={styles.title}>
            <i className="bi bi-box-seam me-2"></i>Items
          </h6>
          <div style={styles.sub}>
            Enterprise item master with GST, barcode, stock controls, reorder
            and audit fields
          </div>
        </div>

        <div style={styles.body}>
          <div
            className="row g-2 mb-2 p-2"
            style={{
              background: "#f8f9fa",
              borderRadius: 10,
              border: "1px solid #e9ecef",
            }}
          >
            <div className="col-md-3">
              <label style={styles.label}>New Item Category</label>
              <div className="input-group input-group-sm">
                <input
                  className="form-control"
                  style={styles.input}
                  value={quick.categoryName}
                  onChange={(e) =>
                    setQuick({ ...quick, categoryName: e.target.value })
                  }
                  placeholder="Example: Finished Goods"
                />
                <button
                  className="btn btn-outline-primary"
                  onClick={() => saveMaster("category")}
                >
                  Add
                </button>
              </div>
            </div>

            <div className="col-md-3">
              <label style={styles.label}>New Item Group</label>
              <div className="input-group input-group-sm">
                <input
                  className="form-control"
                  style={styles.input}
                  value={quick.groupName}
                  onChange={(e) =>
                    setQuick({ ...quick, groupName: e.target.value })
                  }
                  placeholder="Example: Electronics"
                />
                <button
                  className="btn btn-outline-primary"
                  onClick={() => saveMaster("group")}
                >
                  Add
                </button>
              </div>
            </div>

            <div className="col-md-3">
              <label style={styles.label}>New Unit</label>
              <div className="input-group input-group-sm">
                <input
                  className="form-control"
                  style={styles.input}
                  value={quick.unitName}
                  onChange={(e) =>
                    setQuick({ ...quick, unitName: e.target.value })
                  }
                  placeholder="Unit Name"
                />
                <input
                  className="form-control"
                  style={styles.input}
                  value={quick.unitSymbol}
                  onChange={(e) =>
                    setQuick({ ...quick, unitSymbol: e.target.value })
                  }
                  placeholder="Symbol"
                />
                <button
                  className="btn btn-outline-primary"
                  onClick={() => saveMaster("unit")}
                >
                  Add
                </button>
              </div>
            </div>

            <div className="col-md-3">
              <label style={styles.label}>New Warehouse</label>
              <div className="input-group input-group-sm">
                <input
                  className="form-control"
                  style={styles.input}
                  value={quick.warehouseName}
                  onChange={(e) =>
                    setQuick({ ...quick, warehouseName: e.target.value })
                  }
                  placeholder="Main Warehouse"
                />
                <button
                  className="btn btn-outline-primary"
                  onClick={() => saveMaster("warehouse")}
                >
                  Add
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={loadMasters}
                >
                  <i className="bi bi-arrow-clockwise"></i>
                </button>
              </div>
            </div>
          </div>

          {editingId && (
            <div
              className="alert alert-warning py-1 mb-2"
              style={{ fontSize: 11 }}
            >
              Editing item #{safeText(editingId)}. Save to update or cancel to
              create a new item.
            </div>
          )}

          <div style={styles.sectionTitle}>Basic Details</div>

          <div className="row g-2 mb-2">
            <div className="col-md-2">
              <label style={styles.label}>Item Code</label>
              <div className="input-group input-group-sm">
                <input
                  className="form-control"
                  style={styles.input}
                  value={form.itemCode}
                  placeholder="Item Code"
                  onChange={(e) =>
                    setForm({ ...form, itemCode: e.target.value })
                  }
                />
                <button
                  className="btn btn-outline-primary"
                  onClick={generateItemCode}
                >
                  Auto
                </button>
              </div>
            </div>

            {input("itemName", "Item Name")}
            {input("hsnCode", "HSN / SAC Code")}

            <div className="col-md-2">
              <label style={styles.label}>Item Type</label>
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.itemType}
                onChange={(e) => setItemType(e.target.value)}
              >
                <option>Finished Goods</option>
                <option>Raw Material</option>
                <option>Service</option>
                <option>Consumable</option>
                <option>Asset</option>
                <option>Semi Finished</option>
                <option>Scrap</option>
              </select>
            </div>

            {select("categoryId", "Item Category", masters.categories, [
              "CategoryName",
            ])}
            {select("groupId", "Item Group", masters.groups, ["GroupName"])}
            {select(
              "unitId",
              "Unit of Measure",
              masters.units,
              ["UnitSymbol", "UnitName"],
              onUnitChange,
            )}
            {input("brandName", "Brand")}

            <div className="col-md-2">
              <label style={styles.label}>Barcode</label>
              <div className="input-group input-group-sm">
                <input
                  className="form-control"
                  style={styles.input}
                  value={form.barcode}
                  placeholder="Barcode"
                  onChange={(e) =>
                    setForm({ ...form, barcode: e.target.value })
                  }
                />
                <button
                  className="btn btn-outline-primary"
                  onClick={generateBarcode}
                >
                  Gen
                </button>
              </div>
            </div>

            {input("sku", "SKU")}

            {select(
              "warehouseId",
              "Warehouse",
              masters.warehouses,
              ["WarehouseName"],
              onWarehouseChange,
              serviceItem,
            )}

            <div className="col-md-2">
              <label style={styles.label}>Item Status</label>
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.itemStatus}
                onChange={(e) =>
                  setForm({ ...form, itemStatus: e.target.value })
                }
              >
                <option>Active</option>
                <option>Inactive</option>
                <option>Discontinued</option>
              </select>
            </div>
          </div>

          <div style={styles.sectionTitle}>GST & Pricing</div>

          <div className="row g-2 mb-2">
            <div className="col-md-2">
              <label style={styles.label}>GST Rate</label>
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.gstRateId}
                onChange={(e) => onGstChange(e.target.value)}
              >
                <option value="">Select GST Rate</option>
                {gstOptions.map((x, i) => (
                  <option key={x.id || i} value={x.id}>
                    {safeText(x.name)} ({x.percent}%)
                  </option>
                ))}
              </select>
            </div>

            {input("costPrice", "Cost Price", "number")}
            {input("salePrice", "Sale Price", "number")}
            {input(
              "openingStock",
              "Opening Stock",
              "number",
              "col-md-2",
              serviceItem,
            )}
            {input(
              "reorderLevel",
              "Reorder Level",
              "number",
              "col-md-2",
              serviceItem,
            )}
            {input(
              "reorderQty",
              "Reorder Qty",
              "number",
              "col-md-2",
              serviceItem,
            )}
            {input("minStock", "Min Stock", "number", "col-md-2", serviceItem)}
            {input("maxStock", "Max Stock", "number", "col-md-2", serviceItem)}

            <div className="col-md-1">
              <label style={styles.label}>GST %</label>
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.gstPercent}
                readOnly
              />
            </div>

            <div className="col-md-2">
              <label style={styles.label}>Opening Value</label>
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={money(openingValue)}
                readOnly
              />
            </div>
          </div>

          <div style={styles.sectionTitle}>Multi UOM</div>

          <div className="row g-2 mb-2">
            {select("baseUnitId", "Base Unit", masters.units, [
              "UnitSymbol",
              "UnitName",
            ])}
            {select("purchaseUnitId", "Purchase Unit", masters.units, [
              "UnitSymbol",
              "UnitName",
            ])}
            {input("purchaseConversion", "Purchase Conv.", "number")}
            {select("salesUnitId", "Sales Unit", masters.units, [
              "UnitSymbol",
              "UnitName",
            ])}
            {input("salesConversion", "Sales Conv.", "number")}
          </div>

          <div style={styles.sectionTitle}>Controls</div>

          <div className="row g-2 mb-2">
            <div className="col-md-12 d-flex flex-wrap">
              {checkbox("maintainStock", "Maintain Stock", serviceItem)}
              {checkbox(
                "allowNegativeStock",
                "Allow Negative Stock",
                serviceItem,
              )}
              {checkbox("isBatchEnabled", "Batch Tracking", serviceItem)}
              {checkbox("isSerialEnabled", "Serial Tracking", serviceItem)}
              {checkbox("isWarrantyApplicable", "Warranty", serviceItem)}
              {checkbox("isExpiryApplicable", "Expiry", serviceItem)}
            </div>
          </div>

          <div style={styles.sectionTitle}>Other Details</div>

          <div className="row g-2 mb-2">
            {input("itemImageUrl", "Image URL", "text", "col-md-4")}

            <div className="col-md-6">
              <label style={styles.label}>Notes</label>
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.notes}
                placeholder="Notes"
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>

            <div className="col-md-2 d-flex gap-1 align-items-end">
              <button
                className={`btn btn-${editingId ? "primary" : "success"} btn-sm flex-fill`}
                style={styles.btn}
                onClick={save}
                disabled={saving}
              >
                <i className="bi bi-check2-circle me-1"></i>
                {saving ? "Saving..." : editingId ? "Update" : "Save"}
              </button>

              {editingId && (
                <button
                  className="btn btn-outline-secondary btn-sm"
                  style={styles.btn}
                  onClick={cancel}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>

          <div className="row g-2 mb-2">
            <div className="col-md-3">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search code, name, HSN, brand, barcode, SKU..."
              />
            </div>

            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="">All Types</option>
                <option>Finished Goods</option>
                <option>Raw Material</option>
                <option>Service</option>
                <option>Consumable</option>
                <option>Asset</option>
                <option>Semi Finished</option>
                <option>Scrap</option>
              </select>
            </div>

            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {masters.categories.map((x, i) => (
                  <option
                    key={val(x, "Id", "id") || i}
                    value={val(x, "Id", "id")}
                  >
                    {safeText(val(x, "CategoryName", "categoryName"))}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All Status</option>
                <option>Active</option>
                <option>Inactive</option>
                <option>Discontinued</option>
              </select>
            </div>

            <div className="col-md-1">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className="col-md-2 d-flex justify-content-end align-items-center">
              <span style={{ fontSize: 10, color: "#6c757d" }}>
                Total: <strong>{rows.length}</strong> | Showing:{" "}
                <strong>{filteredRows.length}</strong>
              </span>
            </div>
          </div>

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  {[
                    "Code",
                    "Name",
                    "Type",
                    "Status",
                    "Brand",
                    "Barcode",
                    "SKU",
                    "Category",
                    "Group",
                    "HSN/SAC",
                    "Unit",
                    "GST %",
                    "Cost",
                    "Sale",
                    "Opening",
                    "Warehouse",
                    "Reorder",
                    "Stock?",
                    "Batch",
                    "Serial",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={styles.th}
                      className={
                        [
                          "GST %",
                          "Cost",
                          "Sale",
                          "Opening",
                          "Reorder",
                        ].includes(h)
                          ? "text-end"
                          : ""
                      }
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {pagedRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="21"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      {loading ? "Loading items..." : "No items found"}
                    </td>
                  </tr>
                ) : (
                  pagedRows.map((r, i) => (
                    <tr key={val(r, "Id", "id") || i}>
                      <td style={{ ...styles.td, fontWeight: 700 }}>
                        {safeText(val(r, "ItemCode", "itemCode"))}
                      </td>
                      <td style={styles.td}>
                        {safeText(val(r, "ItemName", "itemName"))}
                      </td>
                      <td style={styles.td}>
                        {safeText(val(r, "ItemType", "itemType") || "-")}
                      </td>
                      <td style={styles.td}>
                        {safeText(
                          val(r, "ItemStatus", "itemStatus") || "Active",
                        )}
                      </td>
                      <td style={styles.td}>
                        {safeText(val(r, "BrandName", "brandName") || "-")}
                      </td>
                      <td style={styles.td}>
                        {safeText(val(r, "Barcode", "barcode") || "-")}
                      </td>
                      <td style={styles.td}>
                        {safeText(val(r, "SKU", "sku") || "-")}
                      </td>
                      <td style={styles.td}>
                        {safeText(val(r, "CategoryName", "categoryName"))}
                      </td>
                      <td style={styles.td}>
                        {safeText(val(r, "GroupName", "groupName"))}
                      </td>
                      <td style={styles.td}>
                        {safeText(val(r, "HSNCode", "hsnCode"))}
                      </td>
                      <td style={styles.td}>
                        {safeText(
                          val(r, "UnitSymbol", "unitSymbol") ||
                            val(r, "UnitName", "unitName") ||
                            val(r, "Unit", "unit"),
                        )}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {num(
                          val(r, "GSTPercent", "gstPercent", "TaxPercent"),
                        ).toFixed(2)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(val(r, "CostPrice", "costPrice"))}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(val(r, "SalePrice", "salePrice"))}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {num(
                          val(r, "OpeningStock", "openingStock", "OpeningQty"),
                        ).toFixed(2)}
                      </td>
                      <td style={styles.td}>
                        {safeText(
                          val(r, "WarehouseName", "warehouseName") || "-",
                        )}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {num(val(r, "ReorderLevel", "reorderLevel")).toFixed(2)}
                      </td>
                      <td style={styles.td}>
                        {Boolean(val(r, "MaintainStock", "maintainStock"))
                          ? "Yes"
                          : "No"}
                      </td>
                      <td style={styles.td}>
                        {Boolean(val(r, "IsBatchEnabled", "isBatchEnabled"))
                          ? "Yes"
                          : "No"}
                      </td>
                      <td style={styles.td}>
                        {Boolean(val(r, "IsSerialEnabled", "isSerialEnabled"))
                          ? "Yes"
                          : "No"}
                      </td>
                      <td style={styles.td}>
                        <button
                          className="btn btn-outline-dark btn-sm me-1"
                          style={{ fontSize: 10 }}
                          onClick={() => openLedger(r)}
                        >
                          Ledger
                        </button>
                        <button
                          className="btn btn-outline-primary btn-sm me-1"
                          style={{ fontSize: 10 }}
                          onClick={() => editRow(r)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          style={{ fontSize: 10 }}
                          onClick={() => deleteRow(r)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div
            className="d-flex justify-content-between align-items-center mt-2"
            style={{ fontSize: 11 }}
          >
            <span>
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
            </span>
            <div>
              <button
                className="btn btn-outline-secondary btn-sm me-1"
                style={styles.btn}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                style={styles.btn}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
